/**
 * Scroll memory — preserve scroll positions across navigations within a tab.
 *
 * Two restore strategies:
 *
 *   "pending"  — only restore when a one-shot pending-restore flag is set.
 *                The flag gets set when navigating from Workspace TO the
 *                ArtifactViewer (via markScrollPendingRestore), then
 *                cleared by Workspace's mount effect once consumed. Use
 *                this for the global matters list — direct visits to
 *                /workspace (clicking Workspace in nav, deep links) should
 *                land at the top so new arrivals are visible.
 *
 *   "always"   — restore every time the storage key changes. Use this for
 *                per-context state (per-matter artifact list scroll,
 *                per-matter last-selected artifact) where remembering
 *                "where you were in matter X" is expected behavior even
 *                across in-session matter switches.
 *
 * Storage uses sessionStorage so state clears on tab close (a deliberate
 * scope — persistent storage would surface stale positions days later).
 */

import { useEffect, useLayoutEffect, type RefObject } from 'react'

const PENDING_KEY = 'me.scroll.pendingRestore'

export function markScrollPendingRestore(): void {
  try { sessionStorage.setItem(PENDING_KEY, '1') } catch { /* ignore */ }
}

export function isScrollPendingRestore(): boolean {
  try { return sessionStorage.getItem(PENDING_KEY) === '1' } catch { return false }
}

export function clearScrollPendingRestore(): void {
  try { sessionStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
}

/** Detect whether the current page load was a refresh (vs. a normal
 *  navigate). On reload we treat the matters list like an
 *  ArtifactViewer-back: restore the saved scroll. The "direct visits
 *  land at top" rule only applies to NEW navigations, not reloads —
 *  reloads should preserve the user's session position. Cached value
 *  per page-life so subsequent in-app navigations correctly DON'T
 *  count as reloads. */
let pageReloadCached: boolean | null = null
export function isPageReload(): boolean {
  if (pageReloadCached !== null) return pageReloadCached
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    pageReloadCached = nav?.type === 'reload'
  } catch {
    pageReloadCached = false
  }
  return pageReloadCached
}

/** Save scroll position on scroll (debounced 200ms) and optionally restore
 *  on mount or when the key changes. See file header for restore semantics.
 *
 *  The ref's current element must be a scrollable container (overflow:auto/scroll).
 */
export function useScrollMemory(
  ref: RefObject<HTMLElement | null>,
  key: string,
  restore: 'pending' | 'always' = 'pending',
  /** Gate restore on async content-loaded readiness. When the matters
   *  list lives behind react-query, the scrollable container's height
   *  on first paint is shorter than the saved scrollTop — so setting
   *  scrollTop has no effect until content lands. Pass true once the
   *  content is in the DOM; the effect re-fires and the restore
   *  finally takes. Default true preserves the original behavior for
   *  callers that don't care. */
  ready: boolean = true,
): void {
  // Restore — useLayoutEffect so the scroll happens before the browser paints,
  // avoiding a flash at scrollTop=0.
  //
  // When restore is appropriate but there's NO saved value for this key
  // (e.g. first visit to a matter), we explicitly set scrollTop=0
  // rather than leaving the scroller at whatever value it had for the
  // previous key. Otherwise switching from a deep-scrolled context to a
  // fresh one would inherit the old scroll position and show the new
  // content "shifted out of view" until the user manually scrolls.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (!ready) return
    // 'pending' mode: restore on a marked-pending session OR on a page
    // reload. The reload case lets refresh preserve scroll without
    // breaking the "fresh navigations land at top" rule.
    const should =
      restore === 'always' ||
      isScrollPendingRestore() ||
      (restore === 'pending' && isPageReload())
    if (!should) return
    try {
      const saved = sessionStorage.getItem(key)
      el.scrollTop = saved !== null
        ? Math.max(0, parseInt(saved, 10) || 0)
        : 0
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready])

  // Save — debounced. Listener attaches on mount, detaches on unmount.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let timer: number | null = null
    const onScroll = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        try { sessionStorage.setItem(key, String(el.scrollTop)) } catch { /* ignore */ }
        timer = null
      }, 200)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [key])
}

// ─── Last-selected artifact per matter ────────────────────────────────────
//
// Tracks which artifact the user last clicked on for each matter, so when
// they navigate back to that matter the previous selection re-appears.
//
// Stored in localStorage (NOT sessionStorage) — this is "permanent" state
// that should survive tab close and browser restart. Persisting it
// server-side per user is the next step; for now per-browser is fine.

const LAST_ARTIFACT_PREFIX = 'me.lastArtifact.'

export function rememberLastArtifact(matterScope: string | number | null, artifactId: number | null): void {
  if (!matterScope) return
  try {
    if (artifactId) {
      localStorage.setItem(`${LAST_ARTIFACT_PREFIX}${matterScope}`, String(artifactId))
    } else {
      // Explicit deselection (user clicked a narrative group, hit ESC,
      // closed the artifact pane, etc.) → forget the breadcrumb so a
      // later return to this matter doesn't auto-reopen the artifact
      // they just dismissed.
      localStorage.removeItem(`${LAST_ARTIFACT_PREFIX}${matterScope}`)
    }
  } catch { /* ignore */ }
}

export function recallLastArtifact(matterScope: string | number | null): number | null {
  if (!matterScope) return null
  try {
    const v = localStorage.getItem(`${LAST_ARTIFACT_PREFIX}${matterScope}`)
    return v ? parseInt(v, 10) || null : null
  } catch { return null }
}

// ─── Pure decision logic for the remember+recall coordination ──────────
//
// These extract the trickiest part of Workspace's effects (which had two
// distinct race-condition bugs in quick succession) into pure functions so
// tests can exercise the state-transition matrix without React.
//
// `decideRememberAction` answers: when (matter, artifact) state changes,
// what should localStorage do?
//
//   "store"  — write the artifact id (user actively viewing it)
//   "clear"  — remove the breadcrumb (user explicitly deselected)
//   "noop"   — leave storage alone (matter just changed; the recall hasn't
//              fired yet and clearing would race-condition the breadcrumb
//              away before recall can use it)
//
// `decideRecallAction` answers: should we auto-select an artifact on
// matter entry, and if so, which one?
//
//   { kind: 'select', artifactId } — apply this id to the URL
//   { kind: 'wait' }               — data not loaded yet; come back
//   { kind: 'noop' }               — nothing to do (already selected,
//                                    no breadcrumb, breadcrumb invisible,
//                                    or already attempted this matter)

export type RememberAction = 'store' | 'clear' | 'noop'
export type RecallAction =
  | { kind: 'select'; artifactId: number }
  | { kind: 'wait' }
  | { kind: 'noop' }

export function decideRememberAction(args: {
  prevMatterId: string | number | null
  currentMatterId: string | number | null
  currentArtifactId: number | null
}): RememberAction {
  const { prevMatterId, currentMatterId, currentArtifactId } = args
  if (currentMatterId === null) return 'noop'
  if (currentArtifactId) return 'store'
  // artifactId is null. Distinguish:
  //   matter unchanged → user explicitly deselected → clear
  //   matter changed   → recall about to fire, breadcrumb must survive
  return prevMatterId === currentMatterId ? 'clear' : 'noop'
}

export function decideRecallAction(args: {
  selectedMatterId: string | number | null
  selectedArtifactId: number | null
  matterListLoaded: boolean
  artifactsLoaded: boolean
  storedArtifactId: number | null
  visibleArtifactIds: ReadonlySet<number>
  recallAttemptedFor: string | number | null
}): RecallAction {
  const {
    selectedMatterId, selectedArtifactId,
    matterListLoaded, artifactsLoaded,
    storedArtifactId, visibleArtifactIds, recallAttemptedFor,
  } = args
  if (recallAttemptedFor === selectedMatterId) return { kind: 'noop' }
  if (selectedArtifactId !== null) return { kind: 'noop' }
  if (selectedMatterId === null) return { kind: 'noop' }
  if (!matterListLoaded) return { kind: 'wait' }
  if (!artifactsLoaded) return { kind: 'wait' }
  if (storedArtifactId === null) return { kind: 'noop' }
  if (!visibleArtifactIds.has(storedArtifactId)) return { kind: 'noop' }
  return { kind: 'select', artifactId: storedArtifactId }
}
